'use client';

import { useCallback, useRef } from 'react';

interface UseMarketDragDropArgs {
    /** Ordered list of selected market ids. */
    selected: string[];
    setSelected: (ids: string[]) => void;
    min?: number;
    max?: number;
}

interface DragHandlers {
    draggable: true;
    onDragStart: (e: React.DragEvent) => void;
    onDragOver: (e: React.DragEvent) => void;
    onDrop: (e: React.DragEvent) => void;
}

/**
 * Drag-to-reorder + add/remove for an ordered market selection, with min/max
 * guards. Extracts the logic duplicated between Watchlist and AdvancedChart.
 */
export function useMarketDragDrop({ selected, setSelected, min = 1, max = Infinity }: UseMarketDragDropArgs): {
    dragHandlers: (index: number) => DragHandlers;
    add: (id: string) => void;
    remove: (id: string) => void;
    canAdd: boolean;
    canRemove: boolean;
} {
    const dragIndex = useRef<number | null>(null);

    const dragHandlers = useCallback(
        (index: number): DragHandlers => ({
            draggable: true,
            onDragStart: () => {
                dragIndex.current = index;
            },
            onDragOver: (e) => e.preventDefault(),
            onDrop: (e) => {
                e.preventDefault();
                const from = dragIndex.current;
                dragIndex.current = null;
                if (from === null || from === index) return;
                const next = [...selected];
                const [moved] = next.splice(from, 1);
                next.splice(index, 0, moved);
                setSelected(next);
            },
        }),
        [selected, setSelected],
    );

    const add = useCallback(
        (id: string) => {
            if (selected.includes(id) || selected.length >= max) return;
            setSelected([...selected, id]);
        },
        [selected, setSelected, max],
    );

    const remove = useCallback(
        (id: string) => {
            if (selected.length <= min) return;
            setSelected(selected.filter((s) => s !== id));
        },
        [selected, setSelected, min],
    );

    return { dragHandlers, add, remove, canAdd: selected.length < max, canRemove: selected.length > min };
}
